import React, { memo, useEffect, Suspense, lazy, useCallback } from 'react'
import { InstantSearch, useSearchBox, Index, Configure } from 'react-instantsearch'
import type { LiteClient } from 'algoliasearch/lite'
import { SEARCH_CONFIG } from '../../../Config/Search'
import { SearchInput } from './SearchInput'
import { SearchModal } from './SearchModal'
import type { SearchTab } from '../types'
import { appId } from '../../../Api/search/Algolia'
import { ResultsFallback } from './SearchFallback'

const loadSearchResults = () =>
  import(/* webpackChunkName: "search-results" */ './SearchResults')

const SearchResults = lazy(() =>
  loadSearchResults().then((module) => ({
    default: module.SearchResults,
  }))
)

type InstantSearchWrapperProps = {
  searchClient: LiteClient
  query: string
  isModalOpen: boolean
  activeTab: SearchTab
  onQueryChange: (query: string) => void
  onFocus: () => void
  onBlur: () => void
  onMouseDown?: (e: React.MouseEvent) => void
  onClose: () => void
  onTabChange: (tab: SearchTab) => void
}

type SearchBoxControllerProps = {
  query: string
  onQueryChange: (query: string) => void
  onFocus: () => void
  onBlur: () => void
  onMouseDown?: (e: React.MouseEvent) => void
}

const SEARCH_DEBOUNCE_MS = 300
const HITS_PER_PAGE = 10
const INSIGHTS_CONFIG = {
  insightsInitParams: {
    appId,
    useCookie: true,
  },
}
const FUTURE_CONFIG = { preserveSharedStateOnUnmount: true }

const SEARCH_INDEXES = [
  SEARCH_CONFIG.teams,
  SEARCH_CONFIG.posts,
  SEARCH_CONFIG.videos,
  SEARCH_CONFIG.shop,
  SEARCH_CONFIG.general,
]

const SearchBoxController = memo<SearchBoxControllerProps>(
  ({ query, onQueryChange, onFocus, onBlur, onMouseDown }) => {
    const { refine } = useSearchBox()

    useEffect(() => {
      if (query.length === 0) return undefined

      const timeoutId = setTimeout(() => {
        refine(query)
      }, SEARCH_DEBOUNCE_MS)

      return () => clearTimeout(timeoutId)
    }, [query, refine])

    return (
      <SearchInput
        value={query}
        onChange={onQueryChange}
        onFocus={onFocus}
        onBlur={onBlur}
        onMouseDown={onMouseDown}
        placeholder="Search..."
      />
    )
  }
)
SearchBoxController.displayName = 'SearchBoxController'

type SearchIndicesProps = {
  hasQuery: boolean
}

const SearchIndices = memo<SearchIndicesProps>(({ hasQuery }) => {
  if (!hasQuery) return null

  return (
    <>
      <Configure hitsPerPage={HITS_PER_PAGE} />
      {SEARCH_INDEXES.map((indexName) => (
        <Index key={indexName} indexName={indexName}>
          <Configure hitsPerPage={HITS_PER_PAGE} />
        </Index>
      ))}
    </>
  )
})
SearchIndices.displayName = 'SearchIndices'

export const InstantSearchWrapper: React.FC<InstantSearchWrapperProps> = ({
  searchClient,
  query,
  isModalOpen,
  activeTab,
  onQueryChange,
  onFocus,
  onBlur,
  onMouseDown,
  onClose,
  onTabChange,
}) => {
  const hasQuery = query.length > 0
  const handleFocus = useCallback(() => {
    loadSearchResults()
    onFocus()
  }, [onFocus])

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={SEARCH_CONFIG.players}
      insights={INSIGHTS_CONFIG}
      future={FUTURE_CONFIG}
    >
      <SearchIndices hasQuery={hasQuery} />
      <SearchBoxController
        query={query}
        onQueryChange={onQueryChange}
        onFocus={handleFocus}
        onBlur={onBlur}
        onMouseDown={onMouseDown}
      />
      <SearchModal isOpen={isModalOpen} onClose={onClose}>
        <Suspense fallback={<ResultsFallback />}>
          <SearchResults
            query={query}
            activeTab={activeTab}
            onTabChange={onTabChange}
            onClose={onClose}
          />
        </Suspense>
      </SearchModal>
    </InstantSearch>
  )
}
